import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './generated/schema';
import { ApiError, networkError, toApiError } from './errors';

const UNSAFE = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

// ---------- CSRF ----------
let csrfToken: string | null = null;
let csrfInflight: Promise<string | null> | null = null;

async function loadCsrfToken(): Promise<string | null> {
  if (csrfToken) return csrfToken;
  if (!csrfInflight) {
    csrfInflight = globalThis.fetch(new Request('/api/v1/auth/csrf', { credentials: 'same-origin', headers: { Accept: 'application/json' } }))
      .then(async (res) => {
        if (!res.ok) return null;
        const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
        const value = body?.csrf_token ?? body?.token;
        csrfToken = typeof value === 'string' ? value : null;
        return csrfToken;
      })
      .catch(() => null)
      .finally(() => {
        csrfInflight = null;
      });
  }
  return csrfInflight;
}

/** Forget all session-bound client state (sign-out, tenant switch, 401). */
export function resetClientSecurityState(): void {
  csrfToken = null;
}

export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

const securityMiddleware: Middleware = {
  async onRequest({ request }) {
    request.headers.set('Accept', 'application/json, application/problem+json');
    if (UNSAFE.has(request.method)) {
      const token = await loadCsrfToken();
      if (token) request.headers.set('X-CSRF-Token', token);
      if (!request.headers.has('Idempotency-Key')) request.headers.set('Idempotency-Key', newIdempotencyKey());
    }
    return request;
  },
  onResponse({ response }) {
    if (response.status === 401 || response.status === 403) {
      // A rotated or expired session invalidates the cached CSRF token.
      csrfToken = null;
    }
    return response;
  },
};

export const api = createClient<paths>({
  baseUrl: '',
  credentials: 'same-origin',
  fetch: (request: Request) => globalThis.fetch(request),
});
api.use(securityMiddleware);

interface FetchResultLike<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

/**
 * Await an openapi-fetch call and return its data or throw an ApiError.
 * Transport failures become ApiError with status 0.
 */
export async function unwrap<T>(call: Promise<FetchResultLike<T>>): Promise<T> {
  let result: FetchResultLike<T>;
  try {
    result = await call;
  } catch (cause) {
    if (cause instanceof ApiError) throw cause;
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw networkError(cause);
  }
  const { data, error, response } = result;
  if (!response.ok || error !== undefined) {
    throw toApiError(response, error);
  }
  return data as T;
}

/** ETag helpers: the API uses `"v17"` strong tags matching the JSON `version` field. */
export function ifMatch(version: number | string): string {
  return `"v${version}"`;
}
